/**
 * The intro console: the boot screen and the first-run welcome as one surface.
 *
 * It mounts over `#boot` before anything loads as the compact loader (the
 * wordmark and a live boot log). Once the engine runs the backdrop turns
 * translucent so the field wakes behind it and, on a first visit, a single
 * line and "Enter the field" unfold under the centred header; the button
 * bursts it open. With a camera running, holding a hand up fills the
 * button and opens it too (`landing-hand.ts`). The HUD's first-run moments
 * wait for `onOpen`, so nothing plays unseen behind the console.
 *
 * First visit only (`aether.landing.seen` in `localStorage` — clear it to see
 * it again): later visits stay compact and open on their own when boot
 * finishes. After it opens, `#boot` stays attached as
 * `.done` (hidden, no pointer events) so it can never eat a gesture.
 */

import { coarsePointer } from './device-hints';
import { watchHand, type HandWatch } from './landing-hand';

const SEEN_KEY = 'aether.landing.seen';
/** Must match the exit animation's duration in `landing.css`. */
const EXIT_MS = 1150;
/**
 * Calibration has no real progress to report, so the fill eases toward
 * `CALIBRATE_CEILING` with this time constant and completes on `ready`.
 * A cold start takes ~10 s, which this puts at ~80%.
 */
const CALIBRATE_TAU_MS = 5000;
const CALIBRATE_CEILING = 0.92;
/** How long the full bar holds before the hand-charge takes it over. */
const CALIBRATED_HOLD_MS = 600;

export interface ReadyOptions {
  /** Hands in view, when a camera is running: lets a raised hand enter. */
  hands?: () => number;
  /** The console has fully opened onto the stage. */
  onOpen?: () => void;
}

export interface Intro {
  status(text: string): void;
  /**
   * The engine is running but hand tracking is still warming: on a first visit
   * the welcome unfolds now with the button calibrating (its fill creeps up),
   * so the wait reads as part of the entrance. Return visits stay compact.
   */
  reveal(): void;
  /** Everything is warm: arm the button (or open, on a return visit). */
  ready(options?: ReadyOptions): void;
  fail(message: string): void;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function isFirstRun(): boolean {
  // `?welcome` replays the full welcome without clearing storage.
  if (new URLSearchParams(location.search).has('welcome')) return true;
  try {
    return localStorage.getItem(SEEN_KEY) !== '1';
  } catch {
    return true;
  }
}

function remember(): void {
  try {
    localStorage.setItem(SEEN_KEY, '1');
  } catch {
    /* Private mode: showing it again is a better failure than not starting. */
  }
}

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** The scramble running on each node; a newer one replaces it. */
const scrambling = new WeakMap<HTMLElement, number>();

/**
 * Decodes `text` into `node` through a short glyph scramble. A second scramble
 * on the same node cancels the first: on a slow main thread two overlapping
 * intervals can land in either order, which left the armed button reading
 * CALIBRATING.
 */
function scramble(node: HTMLElement, text: string, ms = 520): void {
  window.clearInterval(scrambling.get(node));
  scrambling.delete(node);
  if (reducedMotion()) {
    node.textContent = text;
    return;
  }
  const glyphs = '▚▞░▒▓<>/\\=+*#01';
  // A timer, not rAF: a hidden tab pauses frames, and the label must still land.
  const start = performance.now();
  const timer = window.setInterval(() => {
    const p = Math.min(1, (performance.now() - start) / ms);
    const locked = Math.floor(p * text.length);
    let out = text.slice(0, locked);
    for (let i = locked; i < text.length; i++) {
      out += text[i] === ' ' ? ' ' : glyphs[(Math.random() * glyphs.length) | 0];
    }
    node.textContent = out;
    if (p === 1) {
      window.clearInterval(timer);
      scrambling.delete(node);
    }
  }, 30);
  scrambling.set(node, timer);
}

/**
 * The field lens: the glow and ripples track the pointer, and the key leans
 * toward it. The rect is measured once on entry (the lean itself would skew
 * a live read) and writes are batched to one per frame.
 */
function magnetise(button: HTMLButtonElement): void {
  let rect: DOMRect | null = null;
  let frame = 0;
  let x = 0.5;
  let y = 0.5;

  const write = () => {
    frame = 0;
    button.style.setProperty('--mx', `${x * 100}%`);
    button.style.setProperty('--my', `${y * 100}%`);
    if (reducedMotion()) return;
    button.style.setProperty('--tx', `${(x - 0.5) * 14}px`);
    button.style.setProperty('--ty', `${(y - 0.5) * 10}px`);
  };

  button.addEventListener('pointerenter', () => {
    rect = button.getBoundingClientRect();
  });
  button.addEventListener('pointermove', (event) => {
    if (!rect || !rect.width || !rect.height) return;
    x = (event.clientX - rect.left) / rect.width;
    y = (event.clientY - rect.top) / rect.height;
    if (!frame) frame = requestAnimationFrame(write);
  });
  button.addEventListener('pointerleave', () => {
    rect = null;
    cancelAnimationFrame(frame);
    frame = 0;
    for (const key of ['--mx', '--my', '--tx', '--ty']) button.style.removeProperty(key);
  });
}

/** Builds the console into `root` and returns the nodes the intro drives. */
function build(root: HTMLElement, full: boolean) {
  root.replaceChildren();
  // Everyone waits on the compact loader; the welcome unfolds on `ready`.
  root.classList.add('compact');

  const frame = el('div', 'landing-frame');

  const head = el('div', 'landing-head');
  const titleRow = el('div', 'landing-title');
  titleRow.append(el('span', 'landing-dot'), el('h1', '', 'AETHER'));
  const state = el('span', 'landing-state', 'BOOTING');
  head.append(titleRow, state);
  frame.append(head);

  if (full) {
    frame.append(el('p', 'landing-lede', 'Your hands shape a living fluid. It is waking up behind this panel.'));
  }

  const foot = el('div', 'landing-foot');
  const log = el('p', 'landing-log');
  const statusEl = el('span', '', 'waking the engine…');
  statusEl.id = 'boot-status';
  log.append(el('span', 'landing-prompt', '>'), statusEl, el('span', 'landing-caret'));
  foot.append(log);

  let enter: HTMLButtonElement | null = null;
  let label: HTMLElement | null = null;
  if (full) {
    enter = el('button', 'landing-enter');
    enter.type = 'button';
    enter.disabled = true;
    label = el('span', 'landing-enter-label', 'INITIALISING');
    const arrow = el('span', 'landing-enter-arrow');
    arrow.append(el('span', '', '›'), el('span', '', '›'), el('span', '', '›'));
    enter.append(
      el('span', 'landing-enter-charge'),
      el('span', 'landing-enter-ripples'),
      label,
      arrow,
      el('span', 'landing-enter-sheen'),
    );
    const cta = el('div', 'landing-cta');
    cta.append(enter, el('span', 'landing-hand', 'or raise a hand to the camera'));
    // Keys mean nothing on a phone; what a phone visitor needs to know is that
    // declining the camera still leaves something to play with.
    const note = coarsePointer()
      ? 'camera optional · no camera? drag to stir'
      : 'camera optional · H controls · T tutorial';
    foot.append(
      cta,
      el('p', 'landing-note', note),
      // Shown by the stylesheet only on a phone held upright: the field is 16:9.
      el('p', 'landing-rotate', 'turn your phone sideways — the field is widescreen'),
    );
  }
  frame.append(foot);
  root.append(frame);

  return { state, statusEl, enter, label, anchors: [titleRow, state, log] };
}

/**
 * Runs `change` and glides each node from where it was to where it lands, so
 * the loader's header and log move into the welcome instead of the console
 * appearing a second time. Transform only, on the compositor.
 */
function glide(nodes: HTMLElement[], change: () => void): void {
  const before = nodes.map((node) => node.getBoundingClientRect());
  change();
  if (reducedMotion()) return;
  nodes.forEach((node, i) => {
    const after = node.getBoundingClientRect();
    const dx = before[i].left - after.left;
    const dy = before[i].top - after.top;
    if (!dx && !dy) return;
    node.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], {
      duration: 700,
      easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
    });
  });
}

/** Mounts the intro console into `#boot`, replacing its static fallback. */
export function mountIntro(resumed = false): Intro {
  const root = document.getElementById('boot') ?? document.body.appendChild(el('div', ''));
  root.id = 'boot';
  // The prerendered loader (index.html) already played the entrance, and after
  // the isolation reload it must not play again: never replay it here.
  root.classList.toggle('resumed', resumed || root.childElementCount > 0);
  const full = isFirstRun();
  const ui = build(root, full);
  let opened = false;
  let revealed = false;
  let calibrating = 0;
  let onOpen: (() => void) | undefined;
  let watch: HandWatch | null = null;
  const charge = root.querySelector<HTMLElement>('.landing-enter-charge');

  /** Bursts the console open from (x, y), surging the stage behind it. */
  const open = (x: number, y: number) => {
    if (opened) return;
    opened = true;
    watch?.stop();
    remember();
    if (reducedMotion()) {
      root.classList.add('done');
      onOpen?.();
      return;
    }
    const shock = el('div', 'landing-shock');
    shock.style.setProperty('--ex', `${x}px`);
    shock.style.setProperty('--ey', `${y}px`);
    // The rings end just past the farthest corner, so they sweep the screen.
    const reach = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y));
    shock.style.setProperty('--reach', `${Math.ceil(reach)}px`);
    shock.append(el('span', ''), el('span', ''));
    document.body.append(shock);
    const stage = document.getElementById('stage');
    stage?.classList.add('surge');
    root.classList.add('entering');
    window.setTimeout(() => {
      root.classList.add('done');
      root.classList.remove('entering');
      shock.remove();
      stage?.classList.remove('surge');
      onOpen?.();
    }, EXIT_MS);
  };

  const openFromButton = () => {
    if (!ui.enter || ui.enter.disabled) return;
    const r = ui.enter.getBoundingClientRect();
    open(r.left + r.width / 2, r.top + r.height / 2);
  };

  /** A held hand fills the button and opens it — the first gesture is the way in. */
  const watchForHand = (hands: () => number) => {
    const fill = charge;
    const hint = root.querySelector<HTMLElement>('.landing-hand');
    if (!fill || !hint) return;
    root.classList.add('hand-ready');
    watch = watchHand(
      hands,
      (charge, seen) => {
        fill.style.setProperty('--charge', String(charge));
        root.classList.toggle('hand-seen', seen);
        hint.textContent = seen ? 'hand seen — hold it there…' : 'or raise a hand to the camera';
      },
      openFromButton,
    );
  };

  if (ui.enter) magnetise(ui.enter);
  ui.enter?.addEventListener('click', openFromButton);
  root.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && root.classList.contains('ready')) openFromButton();
  });

  return {
    status(text) {
      ui.statusEl.textContent = text;
    },
    reveal() {
      if (revealed || !ui.label) return;
      revealed = true;
      // The field wakes behind the console while tracking calibrates.
      root.classList.add('awake');
      ui.state.textContent = 'CALIBRATING';
      // The console stays centred: the header and log only glide up to make
      // room for the lede and the button.
      glide(ui.anchors, () => root.classList.add('revealed'));
      scramble(ui.label, 'CALIBRATING');
      const start = performance.now();
      // A timer, not rAF: frames are exactly what is stuttering right now.
      calibrating = window.setInterval(() => {
        const p = 1 - Math.exp(-(performance.now() - start) / CALIBRATE_TAU_MS);
        charge?.style.setProperty('--charge', String(p * CALIBRATE_CEILING));
      }, 100);
    },
    ready(options = {}) {
      onOpen = options.onOpen;
      root.classList.add('ready');
      ui.state.textContent = 'ONLINE';
      ui.statusEl.textContent = 'field stable — all systems nominal';
      if (ui.enter && ui.label) {
        this.reveal();
        window.clearInterval(calibrating);
        charge?.style.setProperty('--charge', '1');
        ui.enter.disabled = false;
        ui.enter.focus();
        scramble(ui.label, 'ENTER THE FIELD');
        // Let the full bar land before the hand-charge (or nothing) takes it.
        window.setTimeout(() => {
          if (opened) return;
          charge?.style.setProperty('--charge', '0');
          if (options.hands) watchForHand(options.hands);
        }, CALIBRATED_HOLD_MS);
      } else {
        window.setTimeout(() => open(innerWidth / 2, innerHeight / 2), 450);
      }
    },
    fail(message) {
      root.classList.add('failed');
      ui.state.textContent = 'FAILED';
      ui.statusEl.textContent = message;
      if (ui.label) ui.label.textContent = 'OFFLINE';
    },
  };
}
